// Shared "Share to social" dialog — per-content-item publishing through the
// distribution broker. Destinations are the facility's connected accounts,
// filtered by plan and by what the content kind can physically go to
// (text/link can't publish to media-only platforms).
// ⚠️ All user-facing copy in this file is draft — flagged for human review
// (Hard Rule 8) before client exposure.
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, Send, X } from "lucide-react";
import { distributionFetch } from "../lib/api";
import {
  loadBestTimes,
  nextSlotOccurrence,
  slotLabel,
  toLocalInputValue,
} from "../lib/bestTimes";

const PLATFORM_LABELS = {
  facebook: { label: "Facebook", icon: "📘" },
  instagram: { label: "Instagram", icon: "📸" },
  youtube: { label: "YouTube", icon: "▶️" },
  tiktok: { label: "TikTok", icon: "🎵" },
  linkedin: { label: "LinkedIn", icon: "💼" },
  x: { label: "X (Twitter)", icon: "𝕏" },
};

// What each platform can accept from the broker's publish adapters today.
const KIND_SUPPORT = {
  facebook: ["text", "link", "image", "video"],
  instagram: ["image", "video"],
  youtube: ["video"],
  tiktok: ["video"],
  linkedin: ["text", "link"],
  x: ["text", "link"],
};

/**
 * props:
 *   facility           — active facility object (facility_id)
 *   content            — { content_id, kind, body, title?, link_url?, media? }
 *   defaultBody        — prefill for the editable post text
 *   onClose()          — dismiss
 */
export function SocialShareDialog({ facility, content, defaultBody, onClose }) {
  const [connections, setConnections] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [selected, setSelected] = useState({}); // account_id -> {platform, name}
  const [body, setBody] = useState(defaultBody || content.body || "");
  const [overrides, setOverrides] = useState({}); // platform -> custom text
  const [customizing, setCustomizing] = useState({}); // platform -> bool
  const [scheduleAt, setScheduleAt] = useState("");
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState(null); // [{name, platform, ok, detail}]
  const [bestTimes, setBestTimes] = useState(null); // /best-times payload or null

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [conns, use] = await Promise.all([
        distributionFetch(`/connections/${facility.facility_id}`),
        distributionFetch(`/usage/${facility.facility_id}`),
      ]);
      setConnections(conns);
      setUsage(use);
    } catch (e) {
      setLoadError(
        e.detail?.status === "not_entitled"
          ? "The Distribution add-on isn't active for this facility."
          : "Could not load connected accounts. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  }, [facility.facility_id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    // Suggestions are an enhancement — a failed fetch just hides them.
    loadBestTimes(facility.facility_id)
      .then(setBestTimes)
      .catch(() => setBestTimes(null));
  }, [facility.facility_id]);

  const destinations = useMemo(() => {
    if (!connections || !usage) return [];
    const out = [];
    for (const p of connections.platforms) {
      const meta = PLATFORM_LABELS[p.platform] || { label: p.platform };
      const inPlan = usage.allowed_platforms?.includes(p.platform);
      const kindOk = (KIND_SUPPORT[p.platform] || []).includes(content.kind);
      for (const a of p.accounts.filter((a) => a.status === "active")) {
        out.push({
          accountId: a.account_id,
          platform: p.platform,
          name: a.name || a.external_account_id,
          icon: meta.icon,
          platformLabel: meta.label,
          disabled: !inPlan || !kindOk,
          reason: !inPlan
            ? "Not in plan"
            : !kindOk
              ? `${meta.label} needs ${(KIND_SUPPORT[p.platform] || []).join("/")} content`
              : null,
        });
      }
    }
    return out;
  }, [connections, usage, content.kind]);

  const toggle = (dest) => {
    setSelected((prev) => {
      const next = { ...prev };
      if (next[dest.accountId]) delete next[dest.accountId];
      else next[dest.accountId] = dest;
      return next;
    });
  };

  const share = async () => {
    setSending(true);
    const chosen = Object.values(selected);
    const outcomes = [];
    for (const dest of chosen) {
      try {
        const resp = await distributionFetch("/publish", {
          method: "POST",
          headers: {},
          body: JSON.stringify({
            facility_id: facility.facility_id,
            platform: dest.platform,
            account_id: dest.accountId,
            content: {
              ...content,
              // one content per destination platform — the broker's default
              // idempotency key (content_id:platform) dedupes re-shares
              body: overrides[dest.platform]?.trim()
                ? overrides[dest.platform]
                : body,
            },
            schedule_at: scheduleAt ? new Date(scheduleAt).toISOString() : null,
          }),
        });
        outcomes.push({
          ...dest,
          ok: true,
          detail: resp.deduplicated
            ? "Already queued (deduplicated)"
            : resp.status === "pending_approval"
              ? "Sent for approval — a facility approver will review it"
              : scheduleAt
                ? "Scheduled"
                : "Queued",
        });
      } catch (e) {
        outcomes.push({
          ...dest,
          ok: false,
          detail:
            e.detail?.status === "over_limit"
              ? "Monthly post limit reached"
              : e.detail?.status === "tiktok_privacy_required"
                ? "TikTok needs the creator's privacy selection — consent step coming soon"
                : e.detail?.message || e.detail?.status || "Failed",
        });
      }
    }
    setResults(outcomes);
    setSending(false);
  };

  const selectedCount = Object.keys(selected).length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900">Share to social</h3>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 text-sky-500 animate-spin" />
            </div>
          ) : loadError ? (
            <div className="text-sm text-red-600">{loadError}</div>
          ) : results ? (
            <div className="space-y-3">
              <ul className="space-y-2">
                {results.map((r) => (
                  <li
                    key={r.accountId}
                    className="flex items-center justify-between text-sm border rounded-lg px-3 py-2"
                  >
                    <span>
                      {r.icon} {r.name}
                      <span className="text-slate-400 ml-1">
                        ({r.platformLabel})
                      </span>
                    </span>
                    <span className={r.ok ? "text-green-600" : "text-red-600"}>
                      {r.detail}
                    </span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-slate-400">
                Queued posts publish within a minute; scheduled posts at their
                set time. Track engagement in the Distribution portal.
              </p>
              <div className="flex justify-end">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 rounded-lg"
                >
                  Done
                </button>
              </div>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Post text
                </label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={5}
                  className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                />
                <p className="text-xs text-slate-400 mt-1">
                  X truncates at 280 characters.
                  {content.link_url ? " The article link is attached." : ""}
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-2">
                  Destinations
                </label>
                {destinations.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    No connected accounts. Connect platforms in the Distribution
                    portal first.
                  </p>
                ) : (
                  <ul className="space-y-2">
                    {destinations.map((d) => (
                      <li key={d.accountId}>
                        <label
                          className={`flex items-center justify-between border rounded-lg px-3 py-2 text-sm ${
                            d.disabled
                              ? "opacity-50 cursor-not-allowed"
                              : "cursor-pointer hover:bg-slate-50"
                          } ${selected[d.accountId] ? "border-sky-500 bg-sky-50" : "border-slate-200"}`}
                        >
                          <span className="flex items-center gap-2">
                            <input
                              type="checkbox"
                              disabled={d.disabled}
                              checked={!!selected[d.accountId]}
                              onChange={() => toggle(d)}
                            />
                            {d.icon} {d.name}
                            <span className="text-slate-400">
                              ({d.platformLabel})
                            </span>
                          </span>
                          {d.reason && (
                            <span className="text-xs text-slate-400">
                              {d.reason}
                            </span>
                          )}
                        </label>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {(() => {
                const selectedPlatforms = [
                  ...new Set(Object.values(selected).map((d) => d.platform)),
                ];
                if (!selectedPlatforms.length) return null;
                return (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-2">
                      Customize per platform (optional — blank uses the text
                      above)
                    </label>
                    <div className="space-y-2">
                      {selectedPlatforms.map((platform) => {
                        const meta = PLATFORM_LABELS[platform] || {
                          label: platform,
                        };
                        const open = !!customizing[platform];
                        const value = overrides[platform] ?? "";
                        const effective = value.trim() ? value : body;
                        return (
                          <div
                            key={platform}
                            className="border border-slate-200 rounded-lg"
                          >
                            <button
                              type="button"
                              onClick={() =>
                                setCustomizing((prev) => ({
                                  ...prev,
                                  [platform]: !prev[platform],
                                }))
                              }
                              className="w-full flex items-center justify-between px-3 py-2 text-sm text-slate-700"
                            >
                              <span>
                                {meta.icon} {meta.label}
                                {value.trim() && (
                                  <span className="ml-2 text-xs text-sky-600">
                                    customized
                                  </span>
                                )}
                              </span>
                              <span className="text-xs text-slate-400">
                                {open ? "Hide" : "Customize"}
                              </span>
                            </button>
                            {open && (
                              <div className="px-3 pb-3">
                                <textarea
                                  value={value}
                                  onChange={(e) =>
                                    setOverrides((prev) => ({
                                      ...prev,
                                      [platform]: e.target.value,
                                    }))
                                  }
                                  placeholder={body}
                                  rows={3}
                                  className="w-full border border-slate-200 rounded-lg p-2 text-sm focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                                />
                                {platform === "x" && (
                                  <p
                                    className={`text-xs mt-1 ${
                                      effective.length > 280
                                        ? "text-red-600"
                                        : "text-slate-400"
                                    }`}
                                  >
                                    {effective.length}/280 characters
                                    {effective.length > 280 &&
                                      " — X will truncate"}
                                  </p>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}

              <div>
                <label className="block text-xs font-medium text-slate-500 mb-1">
                  Schedule (optional — leave empty to publish now)
                </label>
                <input
                  type="datetime-local"
                  value={scheduleAt}
                  onChange={(e) => setScheduleAt(e.target.value)}
                  className="border border-slate-200 rounded-lg px-3 py-2 text-sm"
                />
                {(() => {
                  if (!bestTimes || selectedCount === 0) return null;
                  const chosen = [
                    ...new Set(Object.values(selected).map((d) => d.platform)),
                  ];
                  const chips = [];
                  const seen = new Set();
                  for (const p of chosen) {
                    const info = bestTimes.platforms?.[p];
                    if (!info?.sufficient) continue;
                    for (const slot of info.suggestions.slice(0, 2)) {
                      const key = `${slot.day_of_week}:${slot.hour}`;
                      if (seen.has(key)) continue;
                      seen.add(key);
                      chips.push({ platform: p, slot });
                    }
                  }
                  if (chips.length === 0) {
                    return (
                      <p className="text-xs text-slate-400 mt-1">
                        Best-time suggestions appear once this facility has more
                        posting history to learn from.
                      </p>
                    );
                  }
                  return (
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <span className="text-xs text-slate-400">
                        Usually good:
                      </span>
                      {chips.slice(0, 4).map(({ platform, slot }) => (
                        <button
                          key={`${platform}-${slot.day_of_week}-${slot.hour}`}
                          type="button"
                          onClick={() =>
                            setScheduleAt(
                              toLocalInputValue(nextSlotOccurrence(slot)),
                            )
                          }
                          title={`${PLATFORM_LABELS[platform]?.label || platform}: avg engagement ${slot.avg_engagement} across ${slot.posts} posts`}
                          className="text-xs px-2 py-0.5 rounded-full border border-slate-200 hover:border-sky-400 hover:text-sky-700"
                        >
                          {PLATFORM_LABELS[platform]?.icon} {slotLabel(slot)}
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {usage?.posts_per_month > 0 && (
                <p className="text-xs text-slate-400">
                  {usage.posts_remaining} of {usage.posts_per_month} monthly
                  posts remaining.
                </p>
              )}

              <div className="flex justify-end gap-2">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium bg-slate-100 hover:bg-slate-200 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={share}
                  disabled={sending || selectedCount === 0 || !body.trim()}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-sky-600 hover:bg-sky-700 rounded-lg disabled:opacity-50"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  Share to {selectedCount || ""}{" "}
                  {selectedCount === 1 ? "destination" : "destinations"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
